import { notEmpty } from "@product/scmp-sdk";
import { useRouter } from "next/router";
import type { ChangeEventHandler, FunctionComponent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useAppBarStickyHeightValue } from "~/components/app-bar/hooks";
import { useSearchContext } from "~/components/search/contexts";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import type { SearchEvent } from "~/components/tracking/google-analytics-4/types";

import {
  Container,
  FullWidthBackground,
  StyledInput as input,
  StyledBaseInput,
  StyledClose,
  StyledInputAdornment,
  StyledMagnifier,
} from "./styles";

export type Props = {
  className?: string;
  onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
};

export const SearchInput: FunctionComponent<Props> = ({ className, onChange }) => {
  const { currentValue, searchRouteFunction } = useSearchContext();
  const router = useRouter();

  // Prevent it will refresh the search result when the user type in the input box
  const [value, setValue] = useState(currentValue);

  useEffect(() => {
    setValue(currentValue);
  }, [currentValue]);

  const handleSearch = useCallback(() => {
    const searchPageRoute = searchRouteFunction?.({ value });
    if (!searchPageRoute) return;

    const { asPath, ...restOfSearchPageRoute } = searchPageRoute;
    void router.push(restOfSearchPageRoute, asPath);

    // GA4 21.2.2
    sendGA4Tracking({
      action: "sys",
      category: "search",
      customized_parameters: {
        search_text: value,
      },
      subcategory: "result",
    } as SearchEvent);
  }, [router, searchRouteFunction, value]);

  const handleReset = useCallback(() => {
    const searchPageRoute = searchRouteFunction?.({ value: "" });
    if (!searchPageRoute) return;

    const { asPath, ...restOfSearchPageRoute } = searchPageRoute;
    void router.push(restOfSearchPageRoute, asPath);
  }, [router, searchRouteFunction]);

  const isShowCloseButton = useMemo(() => notEmpty(currentValue) && value, [currentValue, value]);

  const { appBarStickyHeight } = useAppBarStickyHeightValue();
  return (
    <Container $stickyStackedHeight={appBarStickyHeight} className={className}>
      <FullWidthBackground />
      <StyledBaseInput
        endAdornment={
          <StyledInputAdornment>
            {isShowCloseButton ? (
              <StyledClose onClick={() => handleReset()} />
            ) : (
              <StyledMagnifier onClick={() => handleSearch()} />
            )}
          </StyledInputAdornment>
        }
        placeholder="Search SCMP"
        slots={{
          input,
        }}
        type="search"
        value={value}
        onChange={event => {
          setValue(event.target.value);
          onChange?.(event);
        }}
        onKeyDown={event => {
          if (event.key === "Enter") handleSearch();
        }}
      />
    </Container>
  );
};

SearchInput.displayName = "SearchInput";
